import React, { useState, useMemo, useEffect, useCallback } from 'react';

import {
  Button,
  ButtonTheme,
  primaryTheme,
  secondaryTheme,
} from '@repo/ui/components/Buttons';
import { IconSwitcher } from '@repo/ui/components/Icon';
import { H3 } from '@repo/ui/components/Typography';
import { Logo } from '@repo/ui/deprecated/Logo';
import { Modal } from '@repo/ui/deprecated/Modal';
import { Dialog } from '@repo/ui-kit/components/core/Dialog';

import { StyledTheme } from '../../types';
import TextInput from '../TextInput/TextInput';

import {
  Wrapper,
  Header,
  Message,
  Note,
  ButtonWrapper,
  TextInputWrapper,
} from './styles';
import { DecisionModalProps } from './types';

const ESCAPE = 'Escape';
const ENTER = 'Enter';

const getButtonTheme = ({
  type,
  theme,
  position,
}: {
  type?: 'primary' | 'secondary' | 'custom';
  theme?: Partial<ButtonTheme>;
  position: 'first' | 'second';
}): StyledTheme => {
  let baseTheme;
  if (position === 'first' || type === 'secondary' || !type) {
    baseTheme = secondaryTheme;
  }
  if (position === 'second' || type === 'primary') {
    baseTheme = primaryTheme;
  }

  return { ...baseTheme, ...theme };
};

const DecisionModal: React.FC<DecisionModalProps> = ({
  isOpen = false,
  width = '408px',
  logo,
  icon,
  iconTheme,
  title,
  message,
  note,
  firstButton,
  secondButton,
  escapeTrigger = true,
  contentAlignment,
  buttonsAlignment,
  content,
  validationText,
  theme = {},
  onClose,
  className,
  useNewDialog,
}) => {
  const [text, setText] = useState('');

  const firstButtonTheme = useMemo(
    () =>
      firstButton &&
      getButtonTheme({
        type: firstButton.type,
        theme: firstButton.theme,
        position: 'first',
      }),
    [firstButton]
  );
  const secondButtonTheme = useMemo(
    () =>
      secondButton &&
      getButtonTheme({
        type: secondButton.type,
        theme: secondButton.theme,
        position: 'second',
      }),
    [secondButton]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      event.stopPropagation();
      event.preventDefault();

      if (event.key === ESCAPE) {
        if (!escapeTrigger) {
          onClose && onClose();
          return;
        }
        firstButton?.onClick();
        onClose && onClose();
      }
      if (event.key === ENTER) {
        if (!secondButton) {
          firstButton?.onClick();
          onClose && onClose();
        } else {
          secondButton.onClick();
          onClose && onClose();
        }
      }
    },
    [firstButton, secondButton, escapeTrigger, onClose]
  );

  useEffect(() => {
    isOpen && document.addEventListener('keydown', handleKeyDown);

    return (): void => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown, isOpen]);

  const modalContent = (
    <Wrapper className={secondButton && 'two-buttons'} theme={theme}>
      <Header $contentAlignment={contentAlignment}>
        {logo && <Logo />}
        {icon && <IconSwitcher height="30px" icon={icon} theme={iconTheme} />}
        {title && (typeof title === 'string' ? <H3>{title}</H3> : title)}
      </Header>
      {message && (
        <Message noTextOverflow $contentAlignment={contentAlignment}>
          {message}
        </Message>
      )}
      {content ? content : null}
      {note && <Note $contentAlignment={contentAlignment}>{note}</Note>}
      {validationText && (
        <TextInputWrapper>
          <TextInput value={text} onChanging={setText} />
        </TextInputWrapper>
      )}
      <ButtonWrapper $buttonsAlignment={buttonsAlignment}>
        {firstButton && (
          <Button
            icon={
              firstButton.icon ? { component: firstButton.icon } : undefined
            }
            label={firstButton.label}
            name="confirmDecisionButton"
            theme={firstButtonTheme}
            width={firstButton.width}
            onClick={(): void => {
              firstButton.onClick();
              onClose && onClose();
            }}
          />
        )}
        {secondButton && (
          <Button
            disabled={validationText ? validationText !== text : false}
            icon={
              secondButton.icon ? { component: secondButton.icon } : undefined
            }
            label={secondButton.label}
            name="decisionModalSecondaryButton"
            theme={secondButtonTheme}
            width={secondButton.width}
            onClick={(): void => {
              secondButton.onClick();
              onClose && onClose();
            }}
          />
        )}
      </ButtonWrapper>
    </Wrapper>
  );

  if (useNewDialog) {
    return (
      <Dialog.Root
        modal
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) {
            onClose?.();
          }
        }}
      >
        <Dialog.Content
          height="unset"
          showCloseButton={true}
          title="Decision Modal"
          width={width}
        >
          {modalContent}
        </Dialog.Content>
      </Dialog.Root>
    );
  }

  return (
    <Modal
      className={className}
      isOpen={!!isOpen}
      width={width}
      onClose={onClose}
    >
      {modalContent}
    </Modal>
  );
};

export default DecisionModal;
